/**
 * Custom Size Selector — Custom Vinyl Banners
 *
 * Mirrors the Metaflow/Navegaid app's Width/Height fields into the product's
 * native (hidden) Width / Height variant dropdowns. When the customer picks a
 * size in the app:
 *   1. set the native options[Width] + options[Height] selects to the app values
 *   2. dispatch change so Dawn resolves the exact variant (price + cart id)
 *   3. write the matched price into the price element immediately
 *
 * The Width/Height value ranges are configured in the Metaflow app and must
 * match the product's variant options. Theme JS cannot charge an arbitrary
 * price, so the displayed price always equals a real variant's price.
 */
class CustomSizeSelector extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.readout = this.querySelector('[data-banner-readout]');
    this.widthSelect = document.querySelector(
      `#variant-selects-${this.sectionId} select[name="options[Width]"]`
    );
    this.heightSelect = document.querySelector(
      `#variant-selects-${this.sectionId} select[name="options[Height]"]`
    );
    this.priceEl = document.getElementById(`price-${this.sectionId}`);

    this.parseVariants();
    if (!this.widthSelect || !this.heightSelect) return;

    this.widthField = null;
    this.heightField = null;
    this.onChange = this.debounce(this.handleChange.bind(this), 120);

    // The app loads asynchronously and may re-render — keep watching.
    this.bindAppFields();
    this.observer = new MutationObserver(() => this.bindAppFields());
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  // Build a price lookup keyed "<width>x<height>" from the embedded variant data.
  parseVariants() {
    this.priceBySize = {};
    const node = this.querySelector('[data-banner-variants]');
    if (!node) return;
    let data;
    try {
      data = JSON.parse(node.textContent);
    } catch (e) {
      return;
    }
    // Which embedded option (o1/o2) is Width vs Height.
    const wKey = data.opt1 === 'Width' ? 'o1' : 'o2';
    const hKey = wKey === 'o1' ? 'o2' : 'o1';
    (data.variants || []).forEach((v) => {
      if (v.available === false) return;
      this.priceBySize[`${v[wKey]}x${v[hKey]}`] = v.price;
    });
  }

  // Locate the app's Width/Height fields by their label text and (re)attach
  // change listeners.
  bindAppFields() {
    const width = this.findAppField(/^width/i);
    const height = this.findAppField(/^(height|length)/i);

    [width, height].forEach((field) => {
      if (!field || field.dataset.bannerBound === 'true') return;
      field.addEventListener('change', this.onChange);
      field.addEventListener('input', this.onChange);
      field.dataset.bannerBound = 'true';
    });

    if (width) this.widthField = width;
    if (height) this.heightField = height;
  }

  findAppField(labelRe) {
    const containers = document.querySelectorAll('.navegaid-custom-field');
    for (const container of containers) {
      const labels = container.querySelectorAll('label[for]');
      for (const label of labels) {
        if (labelRe.test(label.textContent.trim())) {
          const el = document.getElementById(label.getAttribute('for'));
          if (el) return el;
        }
      }
    }
    return null;
  }

  handleChange() {
    const w = this.widthField?.value;
    const h = this.heightField?.value;

    if (!w || !h) {
      this.setReadout('');
      return;
    }

    const hasW = this.hasOption(this.widthSelect, w);
    const hasH = this.hasOption(this.heightSelect, h);

    if (!hasW || !hasH) {
      this.setReadout("This size isn't available.");
      return;
    }

    this.setReadout('');
    this.setSelect(this.widthSelect, w);
    this.setSelect(this.heightSelect, h);

    const price = this.priceBySize[`${w}x${h}`];
    if (price) this.updatePrice(price);
  }

  hasOption(select, value) {
    return [...select.options].some((o) => o.value === String(value));
  }

  setSelect(select, value) {
    if (select.value === String(value)) return;
    select.value = String(value);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Write the matched variant's formatted price into Dawn's price element so it
  // reflects the chosen size immediately, regardless of the background fetch.
  updatePrice(formattedPrice) {
    if (!this.priceEl || !formattedPrice) return;
    const regular = this.priceEl.querySelectorAll('.price__regular .price-item--regular');
    const targets = regular.length
      ? regular
      : this.priceEl.querySelectorAll('.price-item--regular');
    targets.forEach((el) => {
      el.textContent = formattedPrice;
    });
  }

  setReadout(text) {
    if (this.readout) this.readout.textContent = text;
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }
}

customElements.define('custom-size-selector', CustomSizeSelector);
